import { useCallback, useEffect } from 'react';
import { getAllElementsRecursively, TextElement } from '@bynder-studio/render-core';
import useEditor from '~/hooks/useEditor';
import { isCtrlKey } from '~/helpers/hotKeys';
import { HORIZONTAL_ALIGNMENTS } from 'packages/pages/editor/RightSideMenu/Shots/Text/TextAlignment/utils';
import { useIsModalOpen } from '~/hooks/useIsModalOpen';

export const useAlignmentShortcuts = () => {
    const { creativeModel, manipulationRenderer } = useEditor();
    const isModalOpen = useIsModalOpen();

    const handleKeyDown = useCallback(
        (event) => {
            if (!manipulationRenderer || isModalOpen) {
                return;
            }

            const selectedElements = getAllElementsRecursively(manipulationRenderer.getSelectedElements() || []).filter(
                (el) => el instanceof TextElement,
            ) as TextElement[];

            let prevent = false;

            if (selectedElements.length) {
                selectedElements.forEach(({ id, contentTransform, locked }) => {
                    let params = null;

                    if (event.altKey && isCtrlKey(event)) {
                        switch (event.code) {
                            case 'KeyL': {
                                prevent = true;
                                params = {
                                    contentTransform: {
                                        ...contentTransform,
                                        horizontalAlignment: HORIZONTAL_ALIGNMENTS[0].value,
                                    },
                                };
                                break;
                            }
                            case 'KeyE': {
                                prevent = true;
                                params = {
                                    contentTransform: {
                                        ...contentTransform,
                                        horizontalAlignment: HORIZONTAL_ALIGNMENTS[1].value,
                                    },
                                };
                                break;
                            }
                            case 'KeyR': {
                                prevent = true;
                                params = {
                                    contentTransform: {
                                        ...contentTransform,
                                        horizontalAlignment: HORIZONTAL_ALIGNMENTS[2].value,
                                    },
                                };
                                break;
                            }
                            case 'KeyJ': {
                                prevent = true;
                                params = {
                                    contentTransform: {
                                        ...contentTransform,
                                        horizontalAlignment: HORIZONTAL_ALIGNMENTS[3].value,
                                    },
                                };
                                break;
                            }

                            default:
                                break;
                        }

                        if (params && !locked) {
                            creativeModel.updateElement(id, params);
                        }
                    }
                });
            }

            if (prevent) {
                event.preventDefault();
            }
        },
        [manipulationRenderer, creativeModel],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown, isModalOpen]);
};
